import { AfterViewInit, Component, ElementRef, OnChanges } from '@angular/core';
import * as d3 from 'd3';
import { Utils } from '../../shared/utils';
import { TooltipOptions } from '../../tooltip/tooltip.component';
import { MproChart } from '../chart.model';

export class HorizontalBarChartData {
  public key: string;
  public value: number;
  public tooltip?: TooltipOptions;
}

@Component({
  selector: 'mpro-horziontal-bar-chart',
  templateUrl: './horizontal-bar-chart.component.html',
  styleUrls: ['./horizontal-bar-chart.component.scss'],
})
export class HorizontalBarChartComponent
  extends MproChart<HorizontalBarChartData[]>
  implements OnChanges, AfterViewInit
{
  protected margin = { top: 0, right: 0, bottom: 0, left: 0 };

  constructor(protected el: ElementRef) {
    super(el);
  }

  protected drawChart(): void {
    const data = this.normalizeData(this.data);
    // List of subgroups = header of the csv files = soil condition here
    var subgroups = this.data.map((dataEntry) => dataEntry.key);

    // List of groups = species here = value of the first column called group -> I show them on the X axis
    var groups = 'first';

    // Add X axis
    var x = d3.scaleLinear().domain([0, 100]).range([0, this.width]);

    // Add Y axis
    var y = d3.scaleBand().domain(groups).range([0, this.height]);

    // color palette = one color per subgroup
    var color = d3
      .scaleOrdinal()
      .domain(subgroups)
      .range(this.options.colors.map((color) => Utils.getColorHex(color)));

    //stack the data? --> stack per subgroup
    var stackedData = d3.stack().keys(subgroups)([data]);

    this.addTooltipAndIndexToStackedData(stackedData);

    // this is needed because the rectangles can be associated with 0, which means they won't be visualized
    // if the 1st rectangle's data is 0, we need to round the 2nd one
    let leftmostRectIndex = this.data.findIndex((v) => v.value > 0);
    let rightmostRectIndex =
      this.data.length -
      1 -
      [...this.data].reverse().findIndex((v) => v.value > 0);

    // Show the bars
    this.svg
      .append('g')
      .selectAll('g')
      // Enter in the stack data = loop key per key = group per group
      .data(stackedData)
      .enter()
      .append('g')
      .attr('class', (d: any) => `group ${d.key}`)
      .attr('fill', (d: any) => {
        return color(d.key);
      })
      .selectAll('rect')
      // enter a second time = loop subgroup per subgroup to add all rectangles
      .data((d: any) => {
        return d;
      })
      .enter()
      .append('path')
      .attr('class', 'tooltipTrigger')
      .attr('d', (d: any) => {
        if (d.idx === leftmostRectIndex) {
          return this.leftRoundedRect(
            x(d[0]),
            0,
            x(d[1]) - x(d[0]) - 2,
            this.height,
            3
          );
        }

        if (d.idx === rightmostRectIndex) {
          return this.rightRoundedRect(
            x(d[0]),
            0,
            x(d[1]) - x(d[0]) - 2,
            this.height,
            3
          );
        }

        return this.rightRoundedRect(
          x(d[0]),
          0,
          x(d[1]) - x(d[0]) - 2,
          this.height,
          0
        );
      });
  }

  private rightRoundedRect(x, y, width, height, radius) {
    if (width <= 0) {
      return '';
    }

    // if the width is too little, then don't round the rectangle
    if (width < 3) {
      radius = 0;
    }

    return (
      'M' +
      x +
      ',' +
      y +
      'h' +
      (width - radius) +
      'a' +
      radius +
      ',' +
      radius +
      ' 0 0 1 ' +
      radius +
      ',' +
      radius +
      'v' +
      (height - 2 * radius) +
      'a' +
      radius +
      ',' +
      radius +
      ' 0 0 1 ' +
      -radius +
      ',' +
      radius +
      'h' +
      (radius - width) +
      'z'
    );
  }

  private leftRoundedRect(x, y, width, height, radius) {
    if (width <= 0) {
      return '';
    }

    // if the width is too little, then don't round the rectangle
    if (width < 3) {
      radius = 0;
    }

    return (
      'M' +
      radius +
      ',' +
      y +
      'h' +
      (width - radius) +
      'v' +
      height +
      'h' +
      (radius - width) +
      'a' +
      radius +
      ',' +
      radius +
      ' 0 0 1 ' +
      -radius +
      ',' +
      -radius +
      'v' +
      -(height - 2 * radius) +
      'a' +
      radius +
      ',' +
      radius +
      ' 0 0 1 ' +
      radius +
      ',' +
      -radius +
      'z'
    );
  }

  protected hasData(): boolean {
    return !!(this.data && this.data.length);
  }

  private addTooltipAndIndexToStackedData(stackedData: any[]): void {
    // a bit hacky
    stackedData.forEach((stack, idx) => {
      stack[0]['tooltip'] = this.data.find((d) => d.key === stack.key)?.tooltip;
      stack[0]['idx'] = idx;
    });
  }

  private normalizeData(data: HorizontalBarChartData[]): {
    [key: string]: any;
  } {
    const total = data.reduce((sum, current) => (sum += current.value), 0);
    const normalizedData = data.reduce(
      (initial, current) => ({
        ...initial,
        [current.key]: (current.value / total) * 100,
      }),
      {}
    );

    return normalizedData;
  }
}
